import { Icon } from '@/components/ui/Icon';
import { mapTextToAttachment, useChatbotInput } from '@/hooks/useChatbotInput';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { useChatbotStore } from '@/lib/chat/ChatbotStore';
import { useSelectionDimensions } from '@anaralabs/lector';
import { Highlighter, MessageSquare } from 'lucide-react';
import { useCallback } from 'react';
import { Button } from '../../Button/Button';

type SelectionActionsProps = {
  chatbotId: string;
  onHighlight: () => void;
};

export const SelectionActions = ({
  chatbotId,
  onHighlight,
}: SelectionActionsProps) => {
  const { getDimension } = useSelectionDimensions();
  const { addAttachments } = useChatbotInput();
  const toggle = useChatbotStore((state) => state.toggle);
  const { track } = useAnalytics();

  const handleChatClick = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const dimensions = getDimension();
      if (!dimensions) return;

      const text = dimensions.text;
      const highlights = dimensions.highlights;

      window.getSelection()?.removeAllRanges();

      toggle('chat', true);
      track('attachment_added', { type: 'selection' });
      addAttachments(
        [mapTextToAttachment(chatbotId, text, 'DOCUMENT', highlights)],
        chatbotId,
      );
    },
    [addAttachments, chatbotId, getDimension, toggle, track],
  );

  const handleHighlightClick = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const dimensions = getDimension();
      if (!dimensions) return;

      onHighlight();
      window.getSelection()?.removeAllRanges();
    },
    [getDimension, onHighlight],
  );

  return (
    <div
      className="bg-popover rounded-md shadow-feint border flex flex-row p-[1px] items-center gap-[1px]"
      onTouchStart={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Button
        size="xs"
        className="py-[5px] font-medium hover:bg-popover-hover"
        variant="ghost"
        onClick={() => {
          const dimensions = getDimension();
          if (!dimensions) return;

          toggle('chat', true);

          track('attachment_added', {
            type: 'selection',
          });

          addAttachments(
            [
              mapTextToAttachment(
                chatbotId,
                dimensions.text,
                'NOTE',
                dimensions?.highlights,
              ),
            ],
            chatbotId,
          );
        }}
        onTouchEnd={handleChatClick}
      >
        <Icon as={MessageSquare} />
        Chat
      </Button>
      <div className="bg-border w-px h-[18px]" />
      <Button
        size="xs"
        className="py-[5px] font-medium hover:bg-popover-hover"
        variant="ghost"
        onClick={handleHighlightClick}
        onTouchEnd={handleHighlightClick}
      >
        <Icon as={Highlighter} />
        Highlight
      </Button>
    </div>
  );
};
